import React, { useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  Chip,
} from '@mui/material';
import SwalReact from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { updatePolicyVisibility } from 'services/transparency-config-service';
import { getPolicies } from 'services/policies-service';
import { DotsLoader } from 'Components/DotsLoader';

const MySwal = SwalReact(Swal);

/**
 * Policy Visibility Configuration
 * Configure which policies are visible and at what detail level
 */
export const PolicyVisibility = ({ config, onUpdate, companyKey }) => {

  // Fetch all policies from policy service
  const { data: policiesData, isLoading: loadingPolicies, error: policiesError } = useQuery(
    ['policies', companyKey],
    () => getPolicies(companyKey),
    {
      enabled: !!companyKey,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  // Initialize form
  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      policies: [],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'policies',
  });

  // Enrich policies with saved config
  useEffect(() => {
    if (policiesData?.data && Array.isArray(policiesData.data)) {
      // Defensive: Ensure policyVisibility is an array
      const savedConfig = Array.isArray(config?.policyVisibility)
        ? config.policyVisibility
        : [];

      const enriched = policiesData.data.map((policy) => {
        const existing = savedConfig.find(s => s.policyId === policy._id);
        return {
          policyId: policy._id,
          policyName: policy.name,
          visibility: existing?.visibility || 'hidden',
          showExecutiveSummary: existing?.showExecutiveSummary ?? false,
          showFullContent: existing?.showFullContent ?? false,
        };
      });

      setValue('policies', enriched);
    }
  }, [policiesData, config, setValue]);

  // Save mutation
  const mutation = useMutation(
    (data) => updatePolicyVisibility(companyKey, data.policies),
    {
      onSuccess: () => {
        MySwal.fire({
          title: 'Sucesso!',
          text: 'Configurações de visibilidade das políticas salvas',
          icon: 'success',
        });
        onUpdate();
      },
      onError: (error) => {
        MySwal.fire({
          title: 'Erro!',
          text: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  // Helper to determine if checkbox should be enabled based on visibility level
  const isExecutiveSummaryEnabled = (visibility) =>
    visibility === 'partial' || visibility === 'protected';

  const isFullContentEnabled = (visibility) =>
    visibility === 'protected';

  // Loading state
  if (loadingPolicies) {
    return (
      <Paper sx={{ p: 3 }}>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Paper>
    );
  }

  // Error state
  if (policiesError || !companyKey) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">
          Erro ao carregar políticas. Por favor, tente novamente.
        </Alert>
      </Paper>
    );
  }

  // Empty state
  if (!policiesData?.data || policiesData.data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Visibilidade de Políticas
        </Typography>
        <Alert severity="info">
          Nenhuma política cadastrada ainda. Configure políticas no módulo de Políticas primeiro.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6" gutterBottom>
          Visibilidade de Políticas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure o nível de visibilidade de cada política no Centro de Transparência.
        </Typography>

        {/* Legend */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Níveis de Visibilidade:</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label="Público" color="success" size="small" />
            <Typography variant="caption">Todos podem ver o conteúdo completo</Typography>
            <Chip label="Parcial" color="warning" size="small" />
            <Typography variant="caption">Apenas resumo executivo visível</Typography>
            <Chip label="Protegido" color="info" size="small" />
            <Typography variant="caption">Requer token de acesso</Typography>
            <Chip label="Oculto" color="default" size="small" />
            <Typography variant="caption">Não exibido</Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Política</TableCell>
                <TableCell align="center">Nível de Visibilidade</TableCell>
                <TableCell align="center">
                  <Box>
                    <Typography variant="subtitle2">Exibir Resumo</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Executivo
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box>
                    <Typography variant="subtitle2">Exibir Conteúdo</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Completo
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => {
                const currentVisibility = watch(`policies.${index}.visibility`);
                const summaryEnabled = isExecutiveSummaryEnabled(currentVisibility);
                const contentEnabled = isFullContentEnabled(currentVisibility);

                return (
                  <TableRow key={field.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {field.policyName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        control={control}
                        name={`policies.${index}.visibility`}
                        render={({ field: { onChange, value } }) => (
                          <Select
                            value={value}
                            onChange={onChange}
                            size="small"
                            sx={{ minWidth: 120 }}
                          >
                            <MenuItem value="public">
                              <Chip label="Público" color="success" size="small" />
                            </MenuItem>
                            <MenuItem value="partial">
                              <Chip label="Parcial" color="warning" size="small" />
                            </MenuItem>
                            <MenuItem value="protected">
                              <Chip label="Protegido" color="info" size="small" />
                            </MenuItem>
                            <MenuItem value="hidden">
                              <Chip label="Oculto" color="default" size="small" />
                            </MenuItem>
                          </Select>
                        )}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        control={control}
                        name={`policies.${index}.showExecutiveSummary`}
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            checked={value}
                            onChange={onChange}
                            disabled={!summaryEnabled}
                            color="primary"
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        control={control}
                        name={`policies.${index}.showFullContent`}
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            checked={value}
                            onChange={onChange}
                            disabled={!contentEnabled}
                            color="primary"
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? <DotsLoader /> : 'Salvar'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};
